import React, { type CSSProperties } from "react";
import {
    Mail,
    MessageCircle,
    MessageSquare,
    Phone,
    ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ContactDetails } from "../../../types/business-content.types";
import {
    buildSmsEnquiryHref,
    buildWhatsAppEnquiryHref,
    normalisePhoneNumberForSms,
} from "../../products/ProductInfoCard/ProductInfoCard.helpers";

export type SimpleContactMethod = "call" | "sms" | "whatsapp" | "email";

export type SimpleContactColors = {
    accent?: string;
    call?: string;
    phone?: string;
    sms?: string;
    whatsapp?: string;
    email?: string;
    background?: string;
    surface?: string;
    text?: string;
    mutedText?: string;
    border?: string;
    divider?: string;
    shadow?: string;
    buttonText?: string;
    iconBackground?: string;
    supportIconBackground?: string;
};

export type SimpleContactTemplateValues = Record<
    string,
    string | number | boolean | null | undefined
>;

export type SimpleContactLabels = {
    callTitle?: string;
    callButton?: string;
    smsTitle?: string;
    smsButton?: string;
    whatsappTitle?: string;
    whatsappButton?: string;
    emailTitle?: string;
    emailButton?: string;
    supportTitle?: string;
    supportDescription?: string;
};

export type SimpleContactProps = {
    contactDetails?: ContactDetails;
    phoneNumber?: string;
    smsNumber?: string;
    whatsappNumber?: string;
    email?: string;
    businessName?: string;
    serviceName?: string;
    pageUrl?: string;
    templateValues?: SimpleContactTemplateValues;
    responseTemplate?: string;
    smsResponseTemplate?: string;
    whatsappResponseTemplate?: string;
    emailSubjectTemplate?: string;
    emailResponseTemplate?: string;
    includePageUrlInResponse?: boolean;
    methods?: SimpleContactMethod[];
    showCall?: boolean;
    showSms?: boolean;
    showWhatsApp?: boolean;
    showEmail?: boolean;
    showSupportNote?: boolean;
    labels?: SimpleContactLabels;
    colors?: SimpleContactColors;
    colours?: SimpleContactColors;
    accentColor?: string;
    className?: string;
    style?: CSSProperties;
};

type CssVarStyle = CSSProperties & Record<`--${string}`, string | number | undefined>;

type ContactAction = {
    method: SimpleContactMethod;
    title: string;
    value: string;
    href: string;
    buttonLabel: string;
    icon: LucideIcon;
    color: string;
    iconBackground: string;
    variant: "solid" | "outline";
    target?: string;
    rel?: string;
    ariaLabel: string;
};

const defaultLabels: Required<SimpleContactLabels> = {
    callTitle: "Call us",
    callButton: "Call now",
    smsTitle: "Message us",
    smsButton: "Send SMS",
    whatsappTitle: "WhatsApp us",
    whatsappButton: "Open WhatsApp",
    emailTitle: "Email us",
    emailButton: "Send enquiry",
    supportTitle: "We're here to help with all your needs.",
    supportDescription: "Fast response. Friendly service. Effective solutions.",
};

const defaultColors: Required<SimpleContactColors> = {
    accent: "#e60012",
    call: "#e60012",
    phone: "#e60012",
    sms: "#e60012",
    whatsapp: "#12a538",
    email: "#e60012",
    background: "#ffffff",
    surface: "#ffffff",
    text: "#141414",
    mutedText: "rgba(20, 20, 20, 0.68)",
    border: "rgba(20, 20, 20, 0.12)",
    divider: "rgba(20, 20, 20, 0.16)",
    shadow: "0 16px 44px rgba(17, 24, 39, 0.08)",
    buttonText: "#ffffff",
    iconBackground: "rgba(230, 0, 18, 0.11)",
    supportIconBackground: "rgba(230, 0, 18, 0.11)",
};

const defaultMethodOrder: SimpleContactMethod[] = [
    "call",
    "sms",
    "whatsapp",
    "email",
];

const isVisible = (
    method: SimpleContactMethod,
    methods: SimpleContactMethod[],
    show: boolean,
) => methods.includes(method) && show;

const cleanValue = (value?: string) => value?.trim() || "";

const hexToRgba = (value: string, alpha: number) => {
    const hex = value.trim().replace("#", "");
    const isShortHex = /^[0-9a-f]{3}$/i.test(hex);
    const isLongHex = /^[0-9a-f]{6}$/i.test(hex);

    if (!isShortHex && !isLongHex) return "";

    const fullHex = isShortHex
        ? hex
            .split("")
            .map((char) => char + char)
            .join("")
        : hex;

    const red = Number.parseInt(fullHex.slice(0, 2), 16);
    const green = Number.parseInt(fullHex.slice(2, 4), 16);
    const blue = Number.parseInt(fullHex.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const getSoftColor = (color: string, fallback: string) =>
    hexToRgba(color, 0.11) || fallback;

const getTemplateValue = (
    values: SimpleContactTemplateValues,
    key: string,
) => {
    const directValue = values[key];
    if (directValue !== undefined && directValue !== null) return String(directValue);
    return "";
};

const renderTemplate = (
    template: string,
    values: SimpleContactTemplateValues,
) =>
    template.replace(
        /{{\s*([a-zA-Z0-9_.-]+)\s*}}|{\s*([a-zA-Z0-9_.-]+)\s*}/g,
        (_match, doubleBraceKey, singleBraceKey) =>
            getTemplateValue(values, doubleBraceKey || singleBraceKey),
    );

const appendPageUrl = (message: string, pageUrl: string) => {
    if (!pageUrl || message.includes(pageUrl)) return message;
    return `${message} ${pageUrl}`.trim();
};

const buildTelHref = (phoneNumber: string) => {
    const phone = normalisePhoneNumberForSms(phoneNumber);
    return phone ? `tel:${phone}` : "";
};

const buildMailtoHref = (email: string, subject: string, body: string) => {
    const safeEmail = cleanValue(email);

    if (!safeEmail) return "";

    const params = new URLSearchParams();
    if (subject) params.set("subject", subject);
    if (body) params.set("body", body);

    const query = params.toString();
    return `mailto:${safeEmail}${query ? `?${query}` : ""}`;
};

const getDefaultResponseTemplate = (serviceName?: string) =>
    serviceName
        ? "Hi, I would like to enquire about {serviceName}."
        : "Hi, I would like to make an enquiry.";

const SimpleContact = ({
                           contactDetails,
                           phoneNumber,
                           smsNumber,
                           whatsappNumber,
                           email,
                           businessName,
                           serviceName,
                           pageUrl,
                           templateValues,
                           responseTemplate,
                           smsResponseTemplate,
                           whatsappResponseTemplate,
                           emailSubjectTemplate = "New enquiry",
                           emailResponseTemplate,
                           includePageUrlInResponse = true,
                           methods = defaultMethodOrder,
                           showCall = true,
                           showSms = true,
                           showWhatsApp = true,
                           showEmail = true,
                           showSupportNote = true,
                           labels,
                           colors,
                           colours,
                           accentColor,
                           className = "",
                           style,
                       }: SimpleContactProps) => {
    const mergedColors = {
        ...defaultColors,
        ...colours,
        ...colors,
    };

    const accent = accentColor || mergedColors.accent;
    const callColor = mergedColors.call || mergedColors.phone || accent;
    const smsColor = mergedColors.sms || accent;
    const whatsappColor = mergedColors.whatsapp || "#12a538";
    const emailColor = mergedColors.email || accent;
    const mergedLabels = { ...defaultLabels, ...labels };

    const displayPhone = cleanValue(phoneNumber || contactDetails?.contactNumberOne);
    const displaySmsNumber = cleanValue(smsNumber || displayPhone);
    const displayWhatsAppNumber = cleanValue(whatsappNumber || displayPhone);
    const displayEmail = cleanValue(email || contactDetails?.contactEmail);
    const safePageUrl = cleanValue(pageUrl);
    const defaultResponseTemplate = responseTemplate || getDefaultResponseTemplate(serviceName);

    const values: SimpleContactTemplateValues = {
        businessName,
        serviceName,
        pageUrl: safePageUrl,
        phone: displayPhone,
        phoneNumber: displayPhone,
        smsNumber: displaySmsNumber,
        whatsappNumber: displayWhatsAppNumber,
        email: displayEmail,
        ...templateValues,
    };

    const buildResponse = (template?: string) => {
        const rendered = renderTemplate(template || defaultResponseTemplate, values).trim();
        return includePageUrlInResponse ? appendPageUrl(rendered, safePageUrl) : rendered;
    };

    const smsMessage = buildResponse(smsResponseTemplate);
    const whatsappMessage = buildResponse(whatsappResponseTemplate);
    const emailSubject = renderTemplate(emailSubjectTemplate, values).trim();
    const emailBody = buildResponse(emailResponseTemplate);

    const allActions: ContactAction[] = [
        {
            method: "call",
            title: mergedLabels.callTitle,
            value: displayPhone,
            href: buildTelHref(displayPhone),
            buttonLabel: mergedLabels.callButton,
            icon: Phone,
            color: callColor,
            iconBackground: getSoftColor(callColor, mergedColors.iconBackground),
            variant: "solid",
            ariaLabel: `Call ${displayPhone}`,
        },
        {
            method: "sms",
            title: mergedLabels.smsTitle,
            value: displaySmsNumber,
            href: buildSmsEnquiryHref(displaySmsNumber, smsMessage),
            buttonLabel: mergedLabels.smsButton,
            icon: MessageSquare,
            color: smsColor,
            iconBackground: getSoftColor(smsColor, mergedColors.iconBackground),
            variant: "outline",
            ariaLabel: `Send SMS to ${displaySmsNumber}`,
        },
        {
            method: "whatsapp",
            title: mergedLabels.whatsappTitle,
            value: displayWhatsAppNumber,
            href: buildWhatsAppEnquiryHref(displayWhatsAppNumber, whatsappMessage),
            buttonLabel: mergedLabels.whatsappButton,
            icon: MessageCircle,
            color: whatsappColor,
            iconBackground: getSoftColor(whatsappColor, "rgba(18, 165, 56, 0.12)"),
            variant: "outline",
            target: "_blank",
            rel: "noopener noreferrer",
            ariaLabel: `Open WhatsApp for ${displayWhatsAppNumber}`,
        },
        {
            method: "email",
            title: mergedLabels.emailTitle,
            value: displayEmail,
            href: buildMailtoHref(displayEmail, emailSubject, emailBody),
            buttonLabel: mergedLabels.emailButton,
            icon: Mail,
            color: emailColor,
            iconBackground: getSoftColor(emailColor, mergedColors.iconBackground),
            variant: "solid",
            ariaLabel: `Email ${displayEmail}`,
        },
    ];

    const actions = allActions.filter((action) => {
        if (!action.value || !action.href) return false;

        if (action.method === "call") return isVisible(action.method, methods, showCall);
        if (action.method === "sms") return isVisible(action.method, methods, showSms);
        if (action.method === "whatsapp") {
            return isVisible(action.method, methods, showWhatsApp);
        }

        return isVisible(action.method, methods, showEmail);
    });

    if (!actions.length) return null;

    const rootStyle: CssVarStyle = {
        "--simple-contact-accent": accent,
        "--simple-contact-background": mergedColors.background,
        "--simple-contact-surface": mergedColors.surface,
        "--simple-contact-text": mergedColors.text,
        "--simple-contact-muted-text": mergedColors.mutedText,
        "--simple-contact-border": mergedColors.border,
        "--simple-contact-divider": mergedColors.divider,
        "--simple-contact-shadow": mergedColors.shadow,
        "--simple-contact-button-text": mergedColors.buttonText,
        "--simple-contact-support-icon-background":
            mergedColors.supportIconBackground ||
            getSoftColor(accent, defaultColors.supportIconBackground),
        ...style,
    };

    return (
        <section
            data-component="simple-contact"
            className={`simple-contact ${className}`.trim()}
            style={rootStyle}
        >
            <div className="simple-contact-inner">
                <div className="simple-contact-list">
                    {actions.map((action) => {
                        const Icon = action.icon;
                        const methodStyle: CssVarStyle = {
                            "--simple-contact-method-color": action.color,
                            "--simple-contact-method-icon-background": action.iconBackground,
                        };

                        return (
                            <article
                                key={action.method}
                                className="simple-contact-action"
                                style={methodStyle}
                            >
                                <span className="simple-contact-icon" aria-hidden="true">
                                    <Icon size={27} strokeWidth={2.4} />
                                </span>

                                <div className="simple-contact-content">
                                    <span className="simple-contact-title">{action.title}</span>
                                    <span className="simple-contact-mobile-value">
                                        {action.value}
                                    </span>
                                </div>

                                <span
                                    className="simple-contact-divider"
                                    aria-hidden="true"
                                />

                                <span className="simple-contact-value">{action.value}</span>

                                <a
                                    className={`simple-contact-button simple-contact-button--${action.variant}`}
                                    href={action.href}
                                    target={action.target}
                                    rel={action.rel}
                                    aria-label={action.ariaLabel}
                                >
                                    {action.buttonLabel}
                                </a>
                            </article>
                        );
                    })}
                </div>

                {showSupportNote ? (
                    <div className="simple-contact-support">
                        <span className="simple-contact-support-icon" aria-hidden="true">
                            <ShieldCheck size={29} strokeWidth={2.2} />
                        </span>

                        <div className="simple-contact-support-content">
                            <p className="simple-contact-support-title">
                                {mergedLabels.supportTitle}
                            </p>
                            <p className="simple-contact-support-description">
                                {mergedLabels.supportDescription}
                            </p>
                        </div>
                    </div>
                ) : null}
            </div>
        </section>
    );
};

export default SimpleContact;
